import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ME_KEY, authApi } from "@/shared/api/auth";
import { ApiError, errorMessage } from "@/shared/api/client";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Brand } from "@/shared/ui/mark";

/** A belépés és a regisztráció **két külön bejárat** (rendszerterv 6.); a kód-lépés közös. */

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-(--app-h) w-full max-w-md flex-col px-5 pb-10 pt-[18px]">
      <div className="mb-8">
        <Brand />
      </div>
      <div className="flex flex-col gap-6">{children}</div>
    </div>
  );
}

export function AuthTitle({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-[28px] font-extrabold leading-[1.1] tracking-[-0.01em]">{title}</h1>
      {children && <p className="text-[15px] leading-[1.5] text-ink-soft">{children}</p>}
    </div>
  );
}

export function EmailField({
  value,
  onChange,
  error,
  hint,
  autoFocus = true,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  hint?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="email">E-mail cím</Label>
      <Input
        id="email"
        type="email"
        autoComplete="email"
        inputMode="email"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error}
        placeholder="te@pelda.hu"
      />
      {error && (
        <p className="text-[13px] text-red" role="alert">
          {error}
        </p>
      )}
      {hint && !error && <p className="text-[12px] text-muted">{hint}</p>}
    </div>
  );
}

/** Kód-lépés: hatjegyű kód → session. Dev-módban a fix kód előre kitöltve. */
export function CodeStep({
  email,
  next,
  devCode,
  project,
  onBack,
}: {
  email: string;
  next: string;
  devCode: string | null;
  project?: string;
  onBack: () => void;
}) {
  const [code, setCode] = useState(devCode ?? "");
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  /** Az előre kitöltött dev-kódot nem küldjük be magától — a felhasználó lássa, mi történik. */
  const edited = useRef(!devCode);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const verify = useMutation({
    mutationFn: (c: string) => authApi.verify(email, c),
    onSuccess: (r) => {
      qc.setQueryData(ME_KEY, r.user);
      navigate(next, { replace: true });
    },
    onError: (e) => {
      setError(
        e instanceof ApiError && e.code === "invalid_code"
          ? "Hibás vagy lejárt kód."
          : errorMessage(e),
      );
      setCode("");
      inputRef.current?.focus();
    },
  });
  const resend = useMutation({
    mutationFn: () => authApi.requestCode(email, project),
    onSuccess: (r) => {
      setResent(true);
      if (r.dev_code) setCode(r.dev_code);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  useEffect(() => {
    if (edited.current && code.length === 6 && !verify.isPending) verify.mutate(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (code.length === 6) verify.mutate(code);
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <AuthTitle title="Írd be a kódot">
        Elküldtük ide: <span className="font-semibold text-ink">{email}</span>. A kód 10 percig
        érvényes.
      </AuthTitle>
      {devCode && (
        <p className="rounded-lg border border-line bg-gray px-3 py-2 text-[13px] text-ink-soft">
          <span className="font-semibold text-ink">Fejlesztői mód:</span> fix belépőkód (
          <code className="font-semibold">{devCode}</code>) — előre kitöltöttük, csak lépj be.
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="code">Hatjegyű kód</Label>
        <Input
          ref={inputRef}
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => {
            edited.current = true;
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
          }}
          aria-invalid={!!error}
          className="h-14 text-center text-[24px] font-extrabold tracking-[0.2em]"
          placeholder="······"
        />
        {error && (
          <p className="text-[13px] text-red" role="alert">
            {error}
          </p>
        )}
      </div>
      <Button type="submit" size="lg" block disabled={code.length !== 6 || verify.isPending}>
        {verify.isPending ? "Ellenőrzés…" : "Belépek"}
      </Button>
      <div className="flex items-center justify-between text-[13px]">
        <button type="button" onClick={onBack} className="font-semibold text-gold-text">
          Másik e-mail cím
        </button>
        {resent ? (
          <span className="text-muted">Új kódot küldtünk.</span>
        ) : (
          <button
            type="button"
            onClick={() => resend.mutate()}
            disabled={resend.isPending}
            className="font-semibold text-gold-text disabled:opacity-50"
          >
            Nem jött meg? Újraküldés
          </button>
        )}
      </div>
    </form>
  );
}
